import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash, CheckCircle, Circle, Clock, ListChecks } from "@phosphor-icons/react";
import { useCollectionSchema, useReferenceData } from "../hooks/useCollectionSchema";
import DynamicForm from "../components/DynamicForm";

const TasksPage = () => {
    const [tasks, setTasks] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [filterProject, setFilterProject] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [formData, setFormData] = useState({});

    // Get dynamic schema
    const { fields, displayName, loading: schemaLoading } = useCollectionSchema("tasks");
    const { references } = useReferenceData(fields);

    useEffect(() => {
        fetchData();
    }, []);

    // Initialize form data when fields change
    useEffect(() => {
        if (fields.length > 0 && !editingTask) {
            initializeFormData();
        }
    }, [fields]);

    const initializeFormData = () => {
        const initial = {};
        fields.forEach(field => {
            initial[field.name] = field.default ?? "";
        });
        setFormData(initial);
    };

    const fetchData = async () => {
        try {
            const [tasksRes, projectsRes] = await Promise.all([
                axios.get(`${API}/tasks`),
                axios.get(`${API}/projects`)
            ]);
            setTasks(tasksRes.data);
            setProjects(projectsRes.data);
        } catch (error) {
            toast.error("Error al cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        const missingRequired = fields
            .filter(f => f.required && (formData[f.name] === "" || formData[f.name] === undefined))
            .map(f => f.label || f.name);
        
        if (missingRequired.length > 0) {
            toast.error(`Campos requeridos: ${missingRequired.join(", ")}`);
            return;
        }

        try {
            if (editingTask) {
                await axios.put(`${API}/tasks/${editingTask.id}`, formData);
                toast.success("Tarea actualizada");
            } else {
                await axios.post(`${API}/tasks`, formData);
                toast.success("Tarea creada");
            }
            fetchData();
            setDialogOpen(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al guardar");
        }
    };

    const handleEdit = (task) => {
        setEditingTask(task);
        const data = {};
        fields.forEach(field => {
            data[field.name] = task[field.name] ?? field.default ?? "";
        });
        setFormData(data);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Eliminar esta tarea?")) return;
        try {
            await axios.delete(`${API}/tasks/${id}`);
            toast.success("Tarea eliminada");
            fetchData();
        } catch (error) {
            toast.error("Error al eliminar");
        }
    };

    const handleStatusChange = async (task, newStatus) => {
        try {
            await axios.put(`${API}/tasks/${task.id}`, { ...task, status: newStatus });
            toast.success("Estado actualizado");
            fetchData();
        } catch (error) {
            toast.error("Error al actualizar estado");
        }
    };

    const resetForm = () => {
        setEditingTask(null);
        initializeFormData();
    };

    const getProjectName = (projectId) => {
        const project = projects.find(p => p.id === projectId);
        return project?.name || "-";
    };

    const getPriorityBadge = (priority) => {
        const priorityMap = {
            "alta": "bg-red-100 text-red-800",
            "media": "bg-yellow-100 text-yellow-800",
            "baja": "bg-green-100 text-green-800"
        };
        return priorityMap[priority] || "bg-gray-100 text-gray-800";
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "completada": return <CheckCircle size={20} weight="fill" className="text-green-500" />;
            case "en-progreso": return <Clock size={20} className="text-yellow-500" />;
            default: return <Circle size={20} className="text-gray-400" />;
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString("es-ES");
    };

    // Filter tasks
    const filteredTasks = tasks.filter(task => {
        const matchesProject = filterProject === "all" || task.project_id === filterProject;
        const matchesStatus = filterStatus === "all" || task.status === filterStatus;
        return matchesProject && matchesStatus;
    });

    // Group tasks by status for columns
    const tasksByStatus = {
        pendiente: filteredTasks.filter(t => t.status === "pendiente"),
        "en-progreso": filteredTasks.filter(t => t.status === "en-progreso"),
        completada: filteredTasks.filter(t => t.status === "completada")
    };

    const pendingCount = tasks.filter(t => t.status === "pendiente").length;

    if (loading || schemaLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in" data-testid="tasks-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl">{displayName}</h1>
                    <p className="text-[#555555] mt-1">
                        {pendingCount} tareas pendientes
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="add-task-btn">
                            <Plus size={20} className="mr-2" />
                            Nueva tarea
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingTask ? "Editar tarea" : "Nueva tarea"}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="mt-4">
                            <DynamicForm 
                                fields={fields}
                                formData={formData}
                                setFormData={setFormData}
                                references={{ ...references, projects }}
                            />
                            <div className="flex gap-3 pt-6">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                                    Cancelar
                                </Button>
                                <Button type="submit" className="btn-primary flex-1" data-testid="task-save-btn">
                                    {editingTask ? "Guardar cambios" : "Crear"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
                <Select value={filterProject} onValueChange={setFilterProject}>
                    <SelectTrigger className="input-industrial w-full md:w-48" data-testid="filter-project-select">
                        <SelectValue placeholder="Obra" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas las obras</SelectItem>
                        {projects.map((project) => (
                            <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="input-industrial w-full md:w-48" data-testid="filter-status-select">
                        <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los estados</SelectItem>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="en-progreso">En progreso</SelectItem>
                        <SelectItem value="completada">Completada</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Kanban Board */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Pending Column */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-4">
                        <Circle size={20} className="text-gray-400" />
                        <h2 className="font-heading font-bold">Pendientes ({tasksByStatus.pendiente.length})</h2>
                    </div>
                    {tasksByStatus.pendiente.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-sm text-[#555555]">
                            Sin tareas pendientes
                        </div>
                    ) : (
                        tasksByStatus.pendiente.map(task => (
                            <TaskCard 
                                key={task.id} 
                                task={task} 
                                getProjectName={getProjectName}
                                getPriorityBadge={getPriorityBadge}
                                formatDate={formatDate}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onStatusChange={handleStatusChange}
                            />
                        ))
                    )}
                </div>

                {/* In Progress Column */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-4">
                        <Clock size={20} className="text-yellow-500" />
                        <h2 className="font-heading font-bold">En progreso ({tasksByStatus["en-progreso"].length})</h2>
                    </div>
                    {tasksByStatus["en-progreso"].length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-sm text-[#555555]">
                            Sin tareas en progreso
                        </div>
                    ) : (
                        tasksByStatus["en-progreso"].map(task => (
                            <TaskCard 
                                key={task.id} 
                                task={task} 
                                getProjectName={getProjectName}
                                getPriorityBadge={getPriorityBadge}
                                formatDate={formatDate}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onStatusChange={handleStatusChange}
                            />
                        ))
                    )}
                </div>

                {/* Completed Column */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-4">
                        <CheckCircle size={20} weight="fill" className="text-green-500" />
                        <h2 className="font-heading font-bold">Completadas ({tasksByStatus.completada.length})</h2>
                    </div>
                    {tasksByStatus.completada.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-sm text-[#555555]">
                            Sin tareas completadas
                        </div>
                    ) : (
                        tasksByStatus.completada.map(task => (
                            <TaskCard 
                                key={task.id} 
                                task={task} 
                                getProjectName={getProjectName}
                                getPriorityBadge={getPriorityBadge}
                                formatDate={formatDate}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onStatusChange={handleStatusChange}
                            />
                        ))
                    )}
                </div>
            </div>

            {filteredTasks.length === 0 && tasks.length > 0 && (
                <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-sm">
                    <ListChecks size={48} className="mx-auto mb-4 text-[#CCCCCC]" />
                    <p className="text-[#555555]">No hay tareas con los filtros seleccionados</p>
                </div>
            )}

            {tasks.length === 0 && (
                <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-sm col-span-full">
                    <ListChecks size={48} className="mx-auto mb-4 text-[#CCCCCC]" />
                    <p className="text-[#555555]">No hay tareas registradas</p>
                </div>
            )}
        </div>
    );
};

// Task Card Component
const TaskCard = ({ task, getProjectName, getPriorityBadge, formatDate, onEdit, onDelete, onStatusChange }) => {
    return (
        <div className="card-industrial" data-testid={`task-card-${task.id}`}>
            <div className="flex items-start justify-between mb-2">
                <h3 className="font-medium">{task.title}</h3>
                <span className={`text-xs font-bold px-2 py-1 rounded-sm ${getPriorityBadge(task.priority)}`}>
                    {(task.priority || "").charAt(0).toUpperCase() + (task.priority || "").slice(1)}
                </span>
            </div>
            
            {task.description && (
                <p className="text-sm text-[#555555] mb-3 line-clamp-2">{task.description}</p>
            )}
            
            <div className="text-xs text-[#555555] space-y-1 mb-3">
                <div>Obra: <span className="font-medium">{getProjectName(task.project_id)}</span></div>
                {task.assigned_to && <div>Asignado: <span className="font-medium">{task.assigned_to}</span></div>}
                {task.due_date && <div>Fecha límite: <span className="font-medium">{formatDate(task.due_date)}</span></div>}
            </div>

            {/* Quick status change buttons */}
            <div className="flex items-center gap-1 mb-3">
                <button
                    onClick={() => onStatusChange(task, "pendiente")}
                    className={`p-1.5 rounded-sm ${task.status === "pendiente" ? "bg-gray-200" : "hover:bg-gray-100"}`}
                    title="Pendiente"
                >
                    <Circle size={16} className={task.status === "pendiente" ? "text-gray-600" : "text-gray-400"} />
                </button>
                <button
                    onClick={() => onStatusChange(task, "en-progreso")}
                    className={`p-1.5 rounded-sm ${task.status === "en-progreso" ? "bg-yellow-100" : "hover:bg-gray-100"}`}
                    title="En progreso"
                >
                    <Clock size={16} className={task.status === "en-progreso" ? "text-yellow-600" : "text-gray-400"} />
                </button>
                <button
                    onClick={() => onStatusChange(task, "completada")}
                    className={`p-1.5 rounded-sm ${task.status === "completada" ? "bg-green-100" : "hover:bg-gray-100"}`}
                    title="Completada"
                >
                    <CheckCircle size={16} weight={task.status === "completada" ? "fill" : "regular"} className={task.status === "completada" ? "text-green-600" : "text-gray-400"} />
                </button>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E5E5E5]">
                <button
                    onClick={() => onEdit(task)}
                    className="p-2 hover:bg-[#F8F9FA] rounded-sm"
                    data-testid={`edit-task-${task.id}`}
                >
                    <Pencil size={16} />
                </button>
                <button
                    onClick={() => onDelete(task.id)}
                    className="p-2 hover:bg-red-50 text-red-600 rounded-sm"
                    data-testid={`delete-task-${task.id}`}
                >
                    <Trash size={16} />
                </button>
            </div>
        </div>
    );
};

export default TasksPage;
